import { useEffect, useRef, useState, useCallback } from "react";

interface BeforeAfterSliderProps {
  beforeSrc: string;
  afterSrc: string;
  beforeAlt?: string;
  afterAlt?: string;
  beforeLabel?: string;
  afterLabel?: string;
  className?: string;
}

export function BeforeAfterSlider({
  beforeSrc,
  afterSrc,
  beforeAlt = "Before",
  afterAlt = "After",
  beforeLabel = "Before",
  afterLabel = "After",
  className = "",
}: BeforeAfterSliderProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState(50);
  const draggingRef = useRef(false);
  const userInteractedRef = useRef(false);
  const autoAnimRef = useRef<number | null>(null);
  const hasAutoRunRef = useRef(false);
  const nudgeTimerRef = useRef<number | null>(null);
  const isVisibleRef = useRef(false);

  const setPos = useCallback((clientX: number) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const pct = ((clientX - rect.left) / rect.width) * 100;
    setPosition(Math.max(0, Math.min(100, pct)));
  }, []);

  const stopAuto = useCallback(() => {
    if (autoAnimRef.current !== null) {
      cancelAnimationFrame(autoAnimRef.current);
      autoAnimRef.current = null;
    }
    if (nudgeTimerRef.current !== null) {
      clearTimeout(nudgeTimerRef.current);
      nudgeTimerRef.current = null;
    }
  }, []);

  // Pointer handlers
  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      if (!draggingRef.current) return;
      setPos(e.clientX);
    };
    const onUp = () => {
      draggingRef.current = false;
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
  }, [setPos]);

  // Auto-play once when visible, then keep nudging subtly as a hint
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const scheduleNudge = () => {
      if (userInteractedRef.current || !isVisibleRef.current) return;
      const delay = 3500 + Math.random() * 2000; // 3.5s–5.5s
      nudgeTimerRef.current = window.setTimeout(() => {
        if (userInteractedRef.current || !isVisibleRef.current) return;
        const start = performance.now();
        const duration = 1400;
        const base = 50;
        const amp = 9; // subtle pulse amplitude
        const tick = (now: number) => {
          if (userInteractedRef.current) {
            stopAuto();
            return;
          }
          const t = Math.min(1, (now - start) / duration);
          // 50 -> 50-amp -> 50+amp -> 50
          let p: number;
          if (t < 0.33) {
            p = base + (-amp) * easeInOut(t / 0.33);
          } else if (t < 0.66) {
            const k = (t - 0.33) / 0.33;
            p = -amp + (amp - -amp) * easeInOut(k) + base;
          } else {
            const k = (t - 0.66) / 0.34;
            p = amp + (0 - amp) * easeInOut(k) + base;
          }
          setPosition(p);
          if (t < 1) {
            autoAnimRef.current = requestAnimationFrame(tick);
          } else {
            autoAnimRef.current = null;
            scheduleNudge();
          }
        };
        autoAnimRef.current = requestAnimationFrame(tick);
      }, delay);
    };

    const obs = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          isVisibleRef.current = entry.isIntersecting;
          if (entry.isIntersecting && !hasAutoRunRef.current && !userInteractedRef.current) {
            hasAutoRunRef.current = true;
            const start = performance.now();
            const duration = 2600;
            const tick = (now: number) => {
              if (userInteractedRef.current) {
                stopAuto();
                return;
              }
              const t = Math.min(1, (now - start) / duration);
              // Ease: go 50 -> 8 -> 92 -> 50
              let p: number;
              if (t < 0.33) {
                const k = t / 0.33;
                p = 50 + (8 - 50) * easeInOut(k);
              } else if (t < 0.66) {
                const k = (t - 0.33) / 0.33;
                p = 8 + (92 - 8) * easeInOut(k);
              } else {
                const k = (t - 0.66) / 0.34;
                p = 92 + (50 - 92) * easeInOut(k);
              }
              setPosition(p);
              if (t < 1) {
                autoAnimRef.current = requestAnimationFrame(tick);
              } else {
                autoAnimRef.current = null;
                scheduleNudge();
              }
            };
            autoAnimRef.current = requestAnimationFrame(tick);
          } else if (entry.isIntersecting && hasAutoRunRef.current && !userInteractedRef.current && autoAnimRef.current === null && nudgeTimerRef.current === null) {
            scheduleNudge();
          }
        }
      },
      { threshold: [0, 0.45] }
    );
    obs.observe(el);
    return () => {
      obs.disconnect();
      stopAuto();
    };
  }, [stopAuto]);

  const onPointerDown = (e: React.PointerEvent) => {
    userInteractedRef.current = true;
    stopAuto();
    draggingRef.current = true;
    (e.target as Element).setPointerCapture?.(e.pointerId);
    setPos(e.clientX);
  };

  return (
    <div
      ref={containerRef}
      className={`relative w-full select-none overflow-hidden rounded-2xl ${className}`}
      onPointerDown={onPointerDown}
      style={{ touchAction: "none", cursor: "ew-resize" }}
    >
      {/* After image (base) */}
      <img
        src={afterSrc}
        alt={afterAlt}
        draggable={false}
        width={1200}
        height={800}
        decoding="async"
        loading="lazy"
        className="block h-auto w-full object-cover"
      />

      {/* Before image (clipped) */}
      <div
        className="absolute inset-0 overflow-hidden"
        style={{ width: `${position}%` }}
      >
        <img
          src={beforeSrc}
          alt={beforeAlt}
          draggable={false}
          width={1200}
          height={800}
          decoding="async"
          loading="lazy"
          className="block h-full w-full object-cover"
          style={{
            width: containerRef.current ? `${containerRef.current.clientWidth}px` : "100%",
            maxWidth: "none",
          }}
        />
      </div>

      {/* Labels */}
      <span className="pointer-events-none absolute left-4 top-4 rounded-full bg-black/70 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-white backdrop-blur-sm">
        {beforeLabel}
      </span>
      <span className="pointer-events-none absolute right-4 top-4 rounded-full bg-white/90 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-black backdrop-blur-sm">
        {afterLabel}
      </span>

      {/* Divider line + handle */}
      <div
        className="pointer-events-none absolute inset-y-0"
        style={{ left: `${position}%`, transform: "translateX(-50%)" }}
      >
        <div className="absolute inset-y-0 left-1/2 w-[2px] -translate-x-1/2 bg-white shadow-[0_0_18px_rgba(0,0,0,0.4)]" />
        <div className="absolute left-1/2 top-1/2 flex h-12 w-12 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white text-black shadow-xl ring-1 ring-black/10">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 6 3 12l6 6" />
            <path d="m15 6 6 6-6 6" />
          </svg>
        </div>
      </div>
    </div>
  );
}

function easeInOut(t: number) {
  return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
}